/** @jsxImportSource @emotion/react */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { ComboBox } from 'components/selects/ComboBox';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomRadio from 'components/inputs/CustomRadio';
import { DefaultProps } from 'pages/md/invest/MdIvesCmpuRegPage';
import { Link, useNavigate } from 'react-router-dom';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { st } from 'components/inputs/CustomInput';
import ModelSimilityPopup from '../../../popup/ModelSimilityPopup';
import { MdModelSpecInfo } from 'models/md/MdIves';

const MdlSpecInfo = ({ elmPrjId }: DefaultProps) => {
  const { t } = useTranslation();
  const { isLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();

  const modelInit = {
    ivesMdlId: '',
    elmPrjId: '',
    mdlNm: '',
    mdlDesc: '',
    cfmYn: '',
    stackingType: '',
    cellType: '',
    finishedCellComposition: '',
    finishedCellDirectionType: '',
    finishedCellLength: '',
    finishedCellWidth: '',
    finishedCellThickness: '',
    finishedCellStackCount: '',
    finishedCellCapacityAh: '',
    finishedCellNominalVoltage: '',
    finishedCellCapacityWh: '',
    finishedCellWeight: '',
    electrodeAnodeFoilThickness: '',
    electrodeCathodeFoilThickness: '',
    electrodeTabWidth: '',
    ndAnodeNotchingType: '',
    ndCathodeNotchingType: '',
    pkgSealingSideType: '',
    pkgPouchThickness: '',
    pkgDailyProductionCapacity: '',
    jigFormationCurrent: '',
    jigFormationChannelCount: '',
    chargeDischargePowerSupplySpec: '',
    chargeDischargeChannelCount: '',
    dgsOrientationType: '',
    dgsCellsPerTray: '',
    eolFoldingType: '',
    eolTapingSideCount: '',
    eolTraysPerBox: '',
    eolCellsPerTray: '',
    atchFileGrId: '',
    rmk: '',
    useYn: '',
    optValCtn1: '',
    optValCtn2: '',
    optValCtn3: '',
    optValCtn4: '',
    optValCtn5: '',
    dataInsUserId: '',
    dataInsUserNm: '',
    dataInsUserIp: '',
    dataInsDtm: '',
    dataUpdUserId: '',
    dataUpdUserNm: '',
    dataUpdUserIp: '',
    dataUpdDtm: '',
  };

  const [errors, setErrors] = useState<any>({});
  const [prjInfo, setPrjInfo] = useState<any>({});
  const [modelSpecInfo, setModelSpecInfo] = useState<MdModelSpecInfo>(modelInit);
  const [mdlSpecCd, setMdlSpecCd] = useState<Code[]>([]);
  const [mdStackingTp, setMdStackingTp] = useState<Code[]>([]);
  const [mdCellTp, setMdCellTp] = useState<Code[]>([]);
  const [mdCptnTpCd, setMdCptnTpCd] = useState<Code[]>([]);
  const [mdDirTp, setMdDirTp] = useState<Code[]>([]);
  const [mdNochTp, setMdNochTp] = useState<Code[]>([]);
  const [mdSideTp, setMdSideTp] = useState<Code[]>([]);
  const [mdDgsDirTp, setMdDgsDirTp] = useState<Code[]>([]);
  const [mdFoldTp, setMdFoldTp] = useState<Code[]>([]);
  const [mdTapgSide, setMdTapgSide] = useState<Code[]>([]);
  const [isOpenSimlModelPopup, setIsOpenSimlModelPopup] = useState<boolean>(false);

  const initCodes = async () => {
    const MDL_SPEC_CD = await getCommonCodeNames('MDL_SPEC_CD'); //	모델 사양코드
    const MD_STACKING_TP = await getCommonCodeNames('MD_STACKING_TP'); //	Stacking Type
    const MD_CELL_TP = await getCommonCodeNames('MD_CELL_TP'); //	Cell Type
    const MD_CPTN_TP_CD = await getCommonCodeNames('MD_CPTN_TP_CD'); //	셀 조성
    const MD_DIR_TP = await getCommonCodeNames('MD_DIR_TP'); //	셀 양/단방향
    const MD_NOCH_TP = await getCommonCodeNames('MD_NOCH_TP'); //	Notching Type
    const MD_SIDE_TP = await getCommonCodeNames('MD_SIDE_TP'); //	PKG 3면/4면
    const MD_DGS_DIR_TP = await getCommonCodeNames('MD_DGS_DIR_TP'); //	DGS 수직/수평
    const MD_FOLD_TP = await getCommonCodeNames('MD_FOLD_TP'); //	Folding Type
    const MD_TAPG_SIDE = await getCommonCodeNames('MD_TAPG_SIDE'); //	EOL Taping 면 수
    setMdlSpecCd(MDL_SPEC_CD);
    setMdStackingTp(MD_STACKING_TP);
    setMdCellTp(MD_CELL_TP);
    setMdCptnTpCd(MD_CPTN_TP_CD);
    setMdDirTp(MD_DIR_TP);
    setMdNochTp(MD_NOCH_TP);
    setMdSideTp(MD_SIDE_TP);
    setMdDgsDirTp(MD_DGS_DIR_TP);
    setMdFoldTp(MD_FOLD_TP);
    setMdTapgSide(MD_TAPG_SIDE);
  };

  const btnSimlModel = () => {
    setIsOpenSimlModelPopup(true);
  };

  const btnList = () => {
    navigate(`/md/ives-cmpu`);
  };

  const btnAdd = () => {
    //
  };

  const btnCopy = () => {
    //
  };

  const btnReset = () => {
    setModelSpecInfo(modelInit);
  };

  const btnDrop = () => {
    //
  };

  const btnComplete = () => {
    //
  };

  const btnSave = () => {
    //
  };

  const handleOnChange = (name: string, value: any) => {
    setModelSpecInfo((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * @param {string} value - 사용자가 입력한 값
   * @param {number} decimalPlaces - 0이면 정수, 1 이상이면 해당 자릿수까지 소수점 허용
   * @returns {string} 정규식 필터링이 완료된 문자열
   */
  const onlyNumInput = (e, decimalPlaces = 0) => {
    const value = e.target.value;
    // 1. 파라미터가 0일 경우: 정수만 허용 (소수점 입력 자체를 차단)
    if (decimalPlaces === 0) {
      const match = value.match(/^\d*/);
      return match ? match[0] : '';
    }

    // 2. 파라미터가 1 이상일 경우: 지정된 소수점 자릿수까지 허용
    if (decimalPlaces >= 1) {
      const regex = new RegExp(`^\\d*(\\.\\d{0,${decimalPlaces}})?`);
      const match = value.match(regex);
      return match ? match[0] : '';
    }

    // 음수 등 잘못된 파라미터가 들어올 경우를 대비한 기본값 처리
    return '';
  };

  useEffect(() => {
    initCodes();
  }, []);

  return (
    <>
      <SubTitleLayout>
        <div>
          <CustomRadio
            id="ver-radio1"
            label={'Ford 1.0'}
            name="version"
            value={'1'}
            checked={prjInfo.version === '1'}
            onChange={(e) => handleOnChange('version', e.target.value)}
          />
          <CustomRadio
            id="ver-radio2"
            label={'Ford 2.0'}
            name="version"
            value={'2'}
            checked={prjInfo.version === '2'}
            onChange={(e) => handleOnChange('version', e.target.value)}
          />
          <CustomRadio
            id="ver-radio3"
            label={'Ford 3.0'}
            name="version"
            value={'3'}
            checked={prjInfo.version === '3'}
            onChange={(e) => handleOnChange('version', e.target.value)}
          />
        </div>
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnList}>
            {t('com.button.목록', '목록')}
          </Button>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnAdd}>
            {t('com.button.추가', '추가')}
          </Button>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnCopy}>
            {t('com.button.복사', '복사')}
          </Button>
          <Button css={IconButton.button} className="icoNone" disableRipple onClick={btnReset}>
            {t('com.button.초기화', '초기화')}
          </Button>
          <Button css={IconButton.button} className="save" disabled={isLoading} onClick={btnSave}>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      </SubTitleLayout>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.모델 사양정보', '모델 사양정보')}</h3>
        </SubTitleGroup>
        <GlobalBtnGroup>
          <ControlBtnGroup className="mr-5">
            <label htmlFor={`main-model-chk`}>
              {t('setup.label.대표모델 지정', '대표모델 지정')}
            </label>
            <div className="txt">
              <Checkbox
                id={`main-model-chk`}
                css={st.checkbox}
                className="custom"
                onChange={(e, c) => {
                  handleOnChange('procChkCd', c ? e.target.value : '');
                }}
              />
            </div>
          </ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="confirm"
            disabled={isLoading}
            onClick={btnSimlModel}
          >
            {t('com.label.유사모델 산출', '유사모델 산출')}
          </Button>
        </GlobalBtnGroup>
      </SubTitleLayout>
      <TableContainer css={tb.customTable} className="colFix9">
        <Table className="!flex">
          <TableBody className="w-1/2 !table">
            <TableRow>
              <TableCell className="header nrb">
                <span>{String(t('mp.label.유사모델', '유사모델'))}</span>
              </TableCell>
              <TableCell className="header"></TableCell>
              <TableCell className="body">
                <button className="link" onClick={btnSimlModel}>
                  H5_5A
                </button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" colSpan={2}>
                <span className="dot">{String(t('mp.label.모델명', '모델명'))}</span>
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="mdlNm"
                  value={modelSpecInfo.mdlNm}
                  onChange={(e) => handleOnChange('mdlNm', e.target.value)}
                  isError={errors?.mdlNm}
                  placeholder={String(
                    t('setup.msg.모델명을 입력해 주세요.', '모델명을 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.모델명을 입력해 주세요.', '모델명을 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" colSpan={2}>
                <span className="dot">{String(t('mp.label.Stacking Type', 'Stacking Type'))}</span>
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdStackingTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.stackingType}
                  onChange={(value) => handleOnChange('stackingType', value)}
                  isError={errors?.stackingType}
                  msgError={String(
                    t('setup.msg.Stacking Type을 선택해 주세요.', 'Stacking Type을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" colSpan={2}>
                <span className="dot">{String(t('mp.label.Cell Type', 'Cell Type'))}</span>
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdCellTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.cellType}
                  onChange={(value) => handleOnChange('cellType', value)}
                  isError={errors?.cellType}
                  msgError={String(
                    t('setup.msg.Cell Type을 선택해 주세요.', 'Cell Type을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={10}>
                {String(t('mp.label.완성 Cell', '완성 Cell'))}
              </TableCell>
              <TableCell className="header">{String(t('mp.label.조성', '조성'))}</TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdCptnTpCd}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.finishedCellComposition}
                  onChange={(value) => handleOnChange('finishedCellComposition', value)}
                  isError={errors?.finishedCellComposition}
                  msgError={String(t('setup.msg.조성을 선택해 주세요.', '조성을 선택해 주세요.'))}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.양/단방향', '양/단방향'))}
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdDirTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.finishedCellDirectionType}
                  onChange={(value) => handleOnChange('finishedCellDirectionType', value)}
                  isError={errors?.finishedCellDirectionType}
                  msgError={String(
                    t('setup.msg.양/단방향을 선택해 주세요.', '양/단방향을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.전장(mm)', '전장(mm)'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellLength"
                  value={modelSpecInfo.finishedCellLength}
                  onChange={(e) => handleOnChange('finishedCellLength', onlyNumInput(e, 1))}
                  isError={errors?.finishedCellLength}
                  placeholder={String(
                    t('setup.msg.전장(mm)을 입력해 주세요.', '전장(mm)을 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.전장(mm)을 입력해 주세요.', '전장(mm)을 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.전폭(mm)', '전폭(mm)'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellWidth"
                  value={modelSpecInfo.finishedCellWidth}
                  onChange={(e) => handleOnChange('finishedCellWidth', onlyNumInput(e, 1))}
                  isError={errors?.finishedCellWidth}
                  placeholder={String(
                    t('setup.msg.전폭(mm)을 입력해 주세요.', '전폭(mm)을 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.전폭(mm)을 입력해 주세요.', '전폭(mm)을 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.두께(mm)', '두께(mm)'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellThickness"
                  value={modelSpecInfo.finishedCellThickness}
                  onChange={(e) => handleOnChange('finishedCellThickness', onlyNumInput(e, 1))}
                  isError={errors?.finishedCellThickness}
                  placeholder={String(
                    t('setup.msg.두께(mm)를 입력해 주세요.', '두께(mm)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.두께(mm)를 입력해 주세요.', '두께(mm)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.Stack 수', 'Stack 수'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellStackCount"
                  value={modelSpecInfo.finishedCellStackCount}
                  onChange={(e) => handleOnChange('finishedCellStackCount', e.target.value)}
                  isError={errors?.finishedCellStackCount}
                  placeholder={String(
                    t('setup.msg.Stack 수를 입력해 주세요.', 'Stack 수를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.Stack 수를 입력해 주세요.', 'Stack 수를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Capacity(Ah)', 'Capacity(Ah)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellCapacityAh"
                  value={modelSpecInfo.finishedCellCapacityAh}
                  onChange={(e) => handleOnChange('finishedCellCapacityAh', onlyNumInput(e, 1))}
                  isError={errors?.finishedCellCapacityAh}
                  placeholder={String(
                    t('setup.msg.Capacity(Ah)을 입력해 주세요.', 'Capacity(Ah)을 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.Capacity(Ah)을 입력해 주세요.', 'Capacity(Ah)을 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Nominal Voltage(V)', 'Nominal Voltage(V)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellNominalVoltage"
                  value={modelSpecInfo.finishedCellNominalVoltage}
                  onChange={(e) => handleOnChange('finishedCellNominalVoltage', e.target.value)}
                  isError={errors?.ivesAmt}
                  placeholder={String(
                    t(
                      'setup.msg.Nominal Voltage(V)을 입력해 주세요.',
                      'Nominal Voltage(V)을 입력해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.Nominal Voltage(V)을 입력해 주세요.',
                      'Nominal Voltage(V)을 입력해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.용량(Wh)', '용량(Wh)'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellCapacityWh"
                  value={
                    Number(modelSpecInfo.finishedCellCapacityAh || 0) *
                    Number(modelSpecInfo.finishedCellNominalVoltage || 0)
                  }
                  readOnly
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.무게(g)', '무게(g)'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="finishedCellWeight"
                  value={modelSpecInfo.finishedCellWeight}
                  onChange={(e) => handleOnChange('finishedCellWeight', e.target.value)}
                  isError={errors?.finishedCellWeight}
                  placeholder={String(
                    t('setup.msg.무게(g)를 입력해 주세요.', '무게(g)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.무게(g)를 입력해 주세요.', '무게(g)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={3}>
                {String(t('mp.label.전극', '전극'))}
              </TableCell>
              <TableCell className="header">
                {String(t('mp.label.양극 Fail 두께(㎛)', '양극 Fail 두께(㎛)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="electrodeAnodeFoilThickness"
                  value={modelSpecInfo.electrodeAnodeFoilThickness}
                  onChange={(e) => handleOnChange('electrodeAnodeFoilThickness', e.target.value)}
                  isError={errors?.electrodeAnodeFoilThickness}
                  placeholder={String(
                    t(
                      'setup.msg.양극 Fail 두께(㎛)를 입력해 주세요.',
                      '양극 Fail 두께(㎛)를 입력해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.양극 Fail 두께(㎛)를 입력해 주세요.',
                      '양극 Fail 두께(㎛)를 입력해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.음극 Fail 두께(㎛)', '음극 Fail 두께(㎛)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="electrodeCathodeFoilThickness"
                  value={modelSpecInfo.electrodeCathodeFoilThickness}
                  onChange={(e) => handleOnChange('electrodeCathodeFoilThickness', e.target.value)}
                  isError={errors?.electrodeCathodeFoilThickness}
                  placeholder={String(
                    t(
                      'setup.msg.음극 Fail 두께(㎛)를 입력해 주세요.',
                      '음극 Fail 두께(㎛)를 입력해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.음극 Fail 두께(㎛)를 입력해 주세요.',
                      '음극 Fail 두께(㎛)를 입력해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Tab 폭(mm)', 'Tab 폭(mm)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="electrodeTabWidth"
                  value={modelSpecInfo.electrodeTabWidth}
                  onChange={(e) => handleOnChange('electrodeTabWidth', e.target.value)}
                  isError={errors?.electrodeTabWidth}
                  placeholder={String(
                    t('setup.msg.Tab 폭(mm)를 입력해 주세요.', 'Tab 폭(mm)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.Tab 폭(mm)를 입력해 주세요.', 'Tab 폭(mm)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
          </TableBody>
          <TableBody className="w-1/2 !table">
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.N&D', 'N&D'))}
              </TableCell>
              <TableCell className="header w200">
                {String(t('mp.label.양극 Notching Type', '양극 Notching Type'))}
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdNochTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.ndAnodeNotchingType}
                  onChange={(value) => handleOnChange('ndAnodeNotchingType', value)}
                  isError={errors?.ndAnodeNotchingType}
                  msgError={String(
                    t(
                      'setup.msg.양극 Notching Type을 선택해 주세요.',
                      '양극 Notching Type을 선택해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.음극 Notching Type', '음극 Notching Type'))}
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdNochTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.ndCathodeNotchingType}
                  onChange={(value) => handleOnChange('ndCathodeNotchingType', value)}
                  isError={errors?.ndCathodeNotchingType}
                  msgError={String(
                    t(
                      'setup.msg.음극 Notching Type을 선택해 주세요.',
                      '음극 Notching Type을 선택해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={3}>
                {String(t('mp.label.PKG', 'PKG'))}
              </TableCell>
              <TableCell className="header">{String(t('mp.label.3면/4면', '3면/4면'))}</TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdSideTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.pkgSealingSideType}
                  onChange={(value) => handleOnChange('pkgSealingSideType', value)}
                  isError={errors?.pkgSealingSideType}
                  msgError={String(
                    t('setup.msg.3면/4면을 선택해 주세요.', '3면/4면을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Pouch 두께(㎛)', 'Pouch 두께(㎛)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="pkgPouchThickness"
                  value={modelSpecInfo.pkgPouchThickness}
                  onChange={(e) => handleOnChange('pkgPouchThickness', onlyNumInput(e))}
                  isError={errors?.pkgPouchThickness}
                  placeholder={String(
                    t(
                      'setup.msg.Pouch 두께(㎛)을 입력해 주세요.',
                      'Pouch 두께(㎛)을 입력해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.Pouch 두께(㎛)을 입력해 주세요.',
                      'Pouch 두께(㎛)을 입력해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.일 생산 Capa(kcelll/일)', '일 생산 Capa(kcelll/일)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="pkgDailyProductionCapacity"
                  value={modelSpecInfo.pkgDailyProductionCapacity}
                  onChange={(e) => handleOnChange('pkgDailyProductionCapacity', onlyNumInput(e, 1))}
                  isError={errors?.pkgDailyProductionCapacity}
                  placeholder={String(
                    t(
                      'setup.msg.일 생산 Capa(kcelll/일)을 선택해 주세요.',
                      '일 생산 Capa(kcelll/일)을 선택해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.일 생산 Capa(kcelll/일)을 선택해 주세요.',
                      '일 생산 Capa(kcelll/일)을 선택해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.Jig Formation', 'Jig Formation'))}
              </TableCell>
              <TableCell className="header">{String(t('mp.label.전류(A)', '전류(A)'))}</TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="jigFormationCurrent"
                  value={modelSpecInfo.jigFormationCurrent}
                  onChange={(e) => handleOnChange('jigFormationCurrent', onlyNumInput(e))}
                  isError={errors?.jigFormationCurrent}
                  placeholder={String(
                    t('setup.msg.전류(A)를 입력해 주세요.', '전류(A)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.전류(A)를 입력해 주세요.', '전류(A)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Ch 수(Ch)', 'Ch 수(Ch)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="jigFormationChannelCount"
                  value={modelSpecInfo.jigFormationChannelCount}
                  onChange={(e) => handleOnChange('jigFormationChannelCount', onlyNumInput(e))}
                  isError={errors?.jigFormationChannelCount}
                  placeholder={String(
                    t('setup.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.층방전기', '층방전기'))}
              </TableCell>
              <TableCell className="header">
                {String(t('mp.label.전원사양(A)', '전원사양(A)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="chargeDischargePowerSupplySpec"
                  value={modelSpecInfo.chargeDischargePowerSupplySpec}
                  onChange={(e) =>
                    handleOnChange('chargeDischargePowerSupplySpec', onlyNumInput(e))
                  }
                  isError={errors?.chargeDischargePowerSupplySpec}
                  placeholder={String(
                    t('setup.msg.전원사양(A)를 입력해 주세요.', '전원사양(A)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.전원사양(A)를 입력해 주세요.', '전원사양(A)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Ch 수(Ch)', 'Ch 수(Ch)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="chargeDischargeChannelCount"
                  value={modelSpecInfo.chargeDischargeChannelCount}
                  onChange={(e) => handleOnChange('chargeDischargeChannelCount', onlyNumInput(e))}
                  isError={errors?.chargeDischargeChannelCount}
                  placeholder={String(
                    t('setup.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                  )}
                  msgError={String(
                    t('setup.msg.Ch 수(Ch)를 입력해 주세요.', 'Ch 수(Ch)를 입력해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">{String(t('mp.label.DGS', 'DGS'))}</TableCell>
              <TableCell className="header">
                {String(t('mp.label.수직/수평', '수직/수평'))}
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdDgsDirTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.dgsOrientationType}
                  onChange={(value) => handleOnChange('dgsOrientationType', value)}
                  isError={errors?.dgsOrientationType}
                  msgError={String(
                    t('setup.msg.수직/수평을 선택해 주세요.', '수직/수평을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.EOL', 'EOL'))}
              </TableCell>
              <TableCell className="header">
                {String(t('mp.label.Folding Type', 'Folding Type'))}
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdFoldTp}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.eolFoldingType}
                  onChange={(value) => handleOnChange('eolFoldingType', value)}
                  isError={errors?.eolFoldingType}
                  msgError={String(
                    t('setup.msg.Folding Type을 선택해 주세요.', 'Folding Type을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Tapping 면 수', 'Tapping 면 수'))}
              </TableCell>
              <TableCell className="body">
                <ComboBox
                  className="w-3/6"
                  options={mdTapgSide}
                  placeholder={String(t('com.label.선택', '선택'))}
                  defaultValue={modelSpecInfo.eolTapingSideCount}
                  onChange={(value) => handleOnChange('eolTapingSideCount', value)}
                  isError={errors?.eolTapingSideCount}
                  msgError={String(
                    t('setup.msg.Tapping 면 수을 선택해 주세요.', 'Tapping 면 수을 선택해 주세요.')
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header" rowSpan={2}>
                {String(t('mp.label.포장재 정보', '포장재 정보'))}
              </TableCell>
              <TableCell className="header">
                {String(t('mp.label.Box당 Tray 수(EA)', 'Box당 Tray 수(EA)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="eolTraysPerBox"
                  value={modelSpecInfo.eolTraysPerBox}
                  onChange={(e) => handleOnChange('eolTraysPerBox', onlyNumInput(e))}
                  isError={errors?.eolTraysPerBox}
                  placeholder={String(
                    t(
                      'setup.msg.Box당 Tray 수(EA)을 선택해 주세요.',
                      'Box당 Tray 수(EA)을 선택해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.Box당 Tray 수(EA)을 선택해 주세요.',
                      'Box당 Tray 수(EA)을 선택해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="header">
                {String(t('mp.label.Tray당 Cell 수(EA)', 'Tray당 Cell 수(EA)'))}
              </TableCell>
              <TableCell className="body">
                <CustomInputWithSearch
                  name="eolCellsPerTray"
                  value={modelSpecInfo.eolCellsPerTray}
                  onChange={(e) => handleOnChange('eolCellsPerTray', onlyNumInput(e))}
                  isError={errors?.eolCellsPerTray}
                  placeholder={String(
                    t(
                      'setup.msg.Tray당 Cell 수(EA)을 선택해 주세요.',
                      'Tray당 Cell 수(EA)을 선택해 주세요.'
                    )
                  )}
                  msgError={String(
                    t(
                      'setup.msg.Tray당 Cell 수(EA)을 선택해 주세요.',
                      'Tray당 Cell 수(EA)을 선택해 주세요.'
                    )
                  )}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} rowSpan={3}></TableCell>
            </TableRow>
            <TableRow></TableRow>
            <TableRow></TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {isOpenSimlModelPopup && (
        <ModelSimilityPopup
          open={isOpenSimlModelPopup}
          close={() => setIsOpenSimlModelPopup(false)}
        />
      )}
    </>
  );
};

export default MdlSpecInfo;
